import { useEffect, useState } from "react";
import { useTheme } from "src/hooks/useTheme";

const getSystemPrefersDark = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: dark)").matches
    : false;

// Resolves useTheme()'s "system"|"light"|"dark" choice down to an actual
// "light"|"dark" value. CSS can defer "system" to prefers-color-scheme on
// its own, but anything that needs a concrete JS value at render time (MUI's
// palette.mode, react-select's styles prop -- neither can read CSS custom
// properties or media queries) needs this resolved explicitly, with a live
// listener so it updates if the OS theme changes while "system" is
// selected, same as the CSS-driven parts of the app already do.
//
// Extracted from MuiThemeBridge.js's original inline logic so react-select
// theming (getSelectStyles, src/utils/common.js) can share the exact same
// resolution instead of duplicating it.
export const useResolvedThemeMode = () => {
  const { theme } = useTheme();
  const [systemPrefersDark, setSystemPrefersDark] = useState(getSystemPrefersDark);

  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemPrefersDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  return theme === "system" ? (systemPrefersDark ? "dark" : "light") : theme;
};
